import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import DashboardLayout from "../layouts/DashboardLayout";
import FundMatchTab from "./dashboard/FundMatchTab";
import FundReachTab from "./dashboard/FundReachTab";
import AdoptionTab from "./dashboard/AdoptionTab";
import AdminHubTab from "./dashboard/AdminHubTab";
import ImpactGrowTab from "./dashboard/ImpactGrowTab";
import ResourceHubTab from "./dashboard/ResourceHubTab";
import DataIntelTab from "./dashboard/DataIntelTab";
import SettingsTab from "./dashboard/SettingsTab";
import PositioningTab from "./dashboard/PositioningTab";
import { useAuth } from "../contexts/AuthContext";
import { MessageCircle, X, Send, Loader2, Bot } from "lucide-react";

const ChatbotWidget = ({ charityBN }) => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: "assistant", content: "Hi! I'm your ImpactPlexus Copilot. I can help you find grants, navigate FundReach, or answer questions about your funding journey. How can I help?" }
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const endRef = useRef(null);

  useEffect(() => {
    if (open) endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, open]);

  const send = async () => {
    const msg = input.trim();
    if (!msg || loading) return;
    setInput("");
    setMessages(prev => [...prev, { role: "user", content: msg }]);
    setLoading(true);
    try {
      const r = await fetch("/api/chatbot/chat", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("authToken") || ""}` },
        body: JSON.stringify({ message: msg, charity_bn: charityBN, session_id: sessionId }),
      });
      const d = await r.json();
      if (d.session_id) setSessionId(d.session_id);
      setMessages(prev => [...prev, { role: "assistant", content: d.reply || "I'm not sure how to help with that right now." }]);
    } catch {
      setMessages(prev => [...prev, { role: "assistant", content: "Sorry, I'm having trouble connecting. Please try again." }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed bottom-6 right-6 z-50 flex flex-col items-end gap-2">
      {open && (
        <div className="w-80 sm:w-96 bg-white rounded-2xl shadow-2xl border border-zinc-200 flex flex-col overflow-hidden" style={{ height: "460px" }}>
          {/* Header */}
          <div className="bg-teal-600 text-white px-4 py-3 flex items-center justify-between shrink-0">
            <div className="flex items-center gap-2">
              <Bot className="h-5 w-5" />
              <div>
                <p className="text-sm font-semibold leading-none">ImpactPlexus Copilot</p>
                <p className="text-xs text-teal-100 mt-0.5">Powered by Qwen3</p>
              </div>
            </div>
            <button onClick={() => setOpen(false)} className="text-teal-100 hover:text-white">
              <X className="h-4 w-4" />
            </button>
          </div>
          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-3 space-y-2 bg-zinc-50">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[80%] rounded-xl px-3 py-2 text-sm ${
                  m.role === "user"
                    ? "bg-teal-600 text-white rounded-br-none"
                    : "bg-white border border-zinc-200 text-zinc-800 rounded-bl-none"
                }`}>
                  {m.content}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="bg-white border border-zinc-200 rounded-xl rounded-bl-none px-3 py-2">
                  <Loader2 className="h-4 w-4 animate-spin text-teal-600" />
                </div>
              </div>
            )}
            <div ref={endRef} />
          </div>
          {/* Input */}
          <div className="p-3 border-t border-zinc-200 flex gap-2 bg-white shrink-0">
            <input
              className="flex-1 text-sm border border-zinc-200 rounded-lg px-3 py-2 outline-none focus:ring-2 focus:ring-teal-500"
              placeholder="Ask me anything…"
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => e.key === "Enter" && !e.shiftKey && send()}
              disabled={loading}
            />
            <button
              onClick={send}
              disabled={loading || !input.trim()}
              className="bg-teal-600 hover:bg-teal-700 disabled:opacity-50 text-white rounded-lg px-3 py-2 flex items-center"
            >
              <Send className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
      {/* Toggle button */}
      <button
        onClick={() => setOpen(v => !v)}
        className="w-14 h-14 rounded-full bg-teal-600 hover:bg-teal-700 text-white shadow-xl flex items-center justify-center transition-all"
        title="Open Copilot chat"
      >
        {open ? <X className="h-6 w-6" /> : <MessageCircle className="h-6 w-6" />}
      </button>
    </div>
  );
};

export const Dashboard = () => {
  const [activeTab, setActiveTab] = useState("fundmatch");
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  // BN from auth user (set at registration/login) or localStorage fallback
  const charityBN = user?.charity_bn || localStorage.getItem('impactplexus_charity_bn') || null;

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case "fundmatch":
        return <FundMatchTab charityBN={charityBN} onNavigate={setActiveTab} />;
      case "fundreach":
        return <FundReachTab charityBN={charityBN} onNavigate={setActiveTab} />;
      case "adminhub":
        return <AdminHubTab />;
      case "impactgrow":
        return <ImpactGrowTab />;
      case "resourcehub":
        return <ResourceHubTab />;
      case "dataintel":
        return <DataIntelTab />;
      case "settings":
        return <SettingsTab />;
      case "positioning":
        return <PositioningTab />;
      case "adoption":
        return <AdoptionTab charityBN={charityBN} />;
      default:
        return <FundMatchTab charityBN={charityBN} />;
    }
  };

  return (
    <DashboardLayout activeTab={activeTab} onTabChange={setActiveTab} user={user} onLogout={handleLogout}>
      {renderTabContent()}
      <ChatbotWidget charityBN={charityBN} />
    </DashboardLayout>
  );
};

export default Dashboard;
